import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { GraduationCap, CaretDown, CaretRight, UsersThree, HandGrabbing, HandArrowDown, ArrowsLeftRight } from "@phosphor-icons/react";
import { toast } from "sonner";

function GrabStudentModal({ srcClassId, student, myClasses, onClose, onDone }) {
  const [targetId, setTargetId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const options = myClasses.filter((c) => c.id !== srcClassId);
  const submit = async () => {
    if (!targetId) { setErr("Pick one of your classes"); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/classes/${srcClassId}/students/${student.id}/migrate`, { target_class_id: targetId });
      toast.success(`${student.name} moved to your class`);
      onDone();
    } catch (e) { setErr(formatApiError(e)); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="grab-student-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="font-bold font-display mb-1">Grab {student.name}</div>
        <p className="text-sm text-[#000000] mb-3">
          They keep their <strong>ID ({student.student_id})</strong>, coins and all progress — they simply continue in your class.
        </p>
        <select data-testid="grab-target-select" className="input-base w-full mb-3" value={targetId} onChange={(e) => setTargetId(e.target.value)}>
          <option value="">Move into which of my classes…</option>
          {options.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.grade}{c.course_title ? ` · ${c.course_title}` : ""}</option>)}
        </select>
        {options.length === 0 && <div className="text-xs text-[#000000] mb-2">You don't have a class to move them into yet — create one under My Classes first.</div>}
        {err && <div className="text-[#FF5100] text-sm mb-2">{err}</div>}
        <div className="flex gap-2">
          <button data-testid="grab-confirm" onClick={submit} disabled={busy || options.length === 0} className="btn-primary flex-1 disabled:opacity-50">
            {busy ? "Moving…" : "Grab student"}
          </button>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function ClassStudents({ cls, myClasses, onChanged }) {
  const [students, setStudents] = useState(null);
  const [err, setErr] = useState("");
  const [grab, setGrab] = useState(null);
  const load = async () => {
    try { const { data } = await api.get(`/classes/${cls.id}/peek`); setStudents(data); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  return (
    <div className="pl-10 pr-4 pb-3" data-testid={`peek-students-${cls.id}`}>
      {err && <div className="text-[#FF5100] text-xs">{err}</div>}
      {!students && !err && <div className="text-xs text-[#000000]">Loading students…</div>}
      {students && students.length === 0 && <div className="text-xs text-[#000000]">No students in this class.</div>}
      <div className="space-y-1.5">
        {(students || []).map((s) => (
          <div key={s.id} data-testid={`peek-student-${s.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-[#E4E4E7] bg-white px-3 py-1.5">
            <div className="min-w-0 flex-1 text-sm">
              <div className="truncate">
                <span className="font-semibold">{s.name}</span>
                <span className="text-xs text-[#000000] ml-2">ID <span className="font-mono font-bold text-[#000000]">{s.student_id}</span></span>
              </div>
              <div className="flex items-center gap-1.5 mt-1">
                <div data-testid={`peek-progress-bar-${s.id}`} title={`${s.progress_percent ?? 0}% complete`}
                  className="flex-1 h-2 rounded-full overflow-hidden"
                  style={{ border: "1px solid #0A0D14", background: "#FFFFFF" }}>
                  <div className="h-full rounded-full" style={{ width: `${s.progress_percent ?? 0}%`, background: "#CCFF00" }} />
                </div>
                <span data-testid={`peek-progress-pct-${s.id}`} className="font-bold text-black shrink-0" style={{ fontSize: "9px", lineHeight: "8px" }}>
                  {s.progress_percent ?? 0}%
                </span>
              </div>
            </div>
            <button data-testid={`grab-student-${s.id}`} onClick={() => setGrab(s)}
              className="btn-secondary text-xs flex items-center gap-1 shrink-0" style={{ padding: "3px 10px" }}
              title="Move this student into one of your classes (keeps ID + progress)">
              <HandGrabbing size={14} weight="bold" /> Grab
            </button>
          </div>
        ))}
      </div>
      {grab && <GrabStudentModal srcClassId={cls.id} student={grab} myClasses={myClasses}
        onClose={() => setGrab(null)} onDone={() => { setGrab(null); load(); onChanged(); }} />}
    </div>
  );
}

function TeacherGroup({ group, myClasses, busyId, onCover, onRelease, onClaim, onCoverAll, onClaimAll, onChanged }) {
  const [open, setOpen] = useState(false);
  const [openStudents, setOpenStudents] = useState({});
  const g = group;
  const totalStudents = g.classes.reduce((s, c) => s + (c.student_count || 0), 0);
  const busy = busyId === g.teacher.id;
  return (
    <div data-testid={`teacher-group-${g.teacher.id}`} className="rounded-2xl border border-[#C4C8CE] bg-white overflow-hidden mb-3">
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#0A0D14] flex-wrap">
        <button data-testid={`toggle-teacher-${g.teacher.id}`} onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 text-left min-w-0 bg-transparent border-0 cursor-pointer">
          {open ? <CaretDown size={14} weight="bold" color="#CCFF00" /> : <CaretRight size={14} weight="bold" color="#CCFF00" />}
          <span className="font-display font-bold truncate" style={{ color: "#CCFF00" }}>{g.teacher.name || g.teacher.email}</span>
          <span className="text-[11px] text-white/60 shrink-0">
            {g.classes.length} class{g.classes.length === 1 ? "" : "es"} · {totalStudents} student{totalStudents === 1 ? "" : "s"}
          </span>
        </button>
        {g.classes.length > 0 && (
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid={`cover-all-${g.teacher.id}`} onClick={() => onCoverAll(g)} disabled={busy}
              className="btn-secondary text-xs flex items-center gap-1 disabled:opacity-50" style={{ padding: "4px 10px" }}
              title="Temporarily cover every class this teacher owns — release them back anytime">
              <ArrowsLeftRight size={13} weight="bold" /> Cover all
            </button>
            <button data-testid={`claim-all-${g.teacher.id}`} onClick={() => onClaimAll(g)} disabled={busy}
              className="btn-secondary text-xs flex items-center gap-1 disabled:opacity-50"
              style={{ padding: "4px 10px", background: "#FEF3C7", color: "#92400E", borderColor: "#FDE68A" }}
              title="Permanently take over every class this teacher owns">
              <HandGrabbing size={13} weight="bold" /> Claim all
            </button>
          </div>
        )}
      </div>
      {open && g.classes.length === 0 && (
        <div className="px-4 py-3 text-sm text-[#000000]">No classes assigned to this teacher.</div>
      )}
      {open && g.classes.map((c) => (
        <div key={c.id} data-testid={`other-class-${c.id}`} className="border-t border-[#E4E4E7]">
          <div className="flex items-center justify-between gap-2 px-4 py-2.5 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <GraduationCap size={17} weight="duotone" color="#84CC16" className="shrink-0" />
                <span className="font-bold font-display truncate">{c.name}</span>
                <span className="text-xs text-[#000000]">{c.grade} · {c.course_title || "—"} · {c.student_count} student{c.student_count === 1 ? "" : "s"}</span>
                {c.covered_by_me && (
                  <span data-testid={`covered-by-me-${c.id}`} className="text-[10px] px-1.5 py-0.5 rounded bg-[#DBEAFE] text-[#1E40AF] font-bold">you're covering</span>
                )}
                {c.covering_teacher_id && !c.covered_by_me && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F4F4F5] text-[#000000] font-bold">covered by {c.covering_teacher_name || "another teacher"}</span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 max-w-md">
                <div data-testid={`other-class-progress-bar-${c.id}`} title={`Class average: ${c.avg_progress ?? 0}%`}
                  className="flex-1 h-2 rounded-full overflow-hidden"
                  style={{ border: "1px solid #0A0D14", background: "#FFFFFF" }}>
                  <div className="h-full rounded-full" style={{ width: `${c.avg_progress ?? 0}%`, background: "#CCFF00" }} />
                </div>
                <span data-testid={`other-class-progress-pct-${c.id}`} className="font-bold text-black shrink-0" style={{ fontSize: "9px", lineHeight: "8px" }}>
                  {c.avg_progress ?? 0}%
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
              <button data-testid={`peek-toggle-${c.id}`}
                onClick={() => setOpenStudents((s) => ({ ...s, [c.id]: !s[c.id] }))}
                className="btn-ghost text-xs flex items-center gap-1 border border-[#C4C8CE] rounded-xl" style={{ padding: "4px 10px" }}
                title="See this class's students to grab them individually">
                <UsersThree size={14} weight="bold" /> Students
              </button>
              {c.covered_by_me ? (
                <button data-testid={`release-class-${c.id}`} onClick={() => onRelease(c)} disabled={busyId === c.id}
                  className="btn-ghost text-xs flex items-center gap-1 text-[#1E40AF] border border-[#BFDBFE] rounded-xl disabled:opacity-50" style={{ padding: "4px 10px" }}
                  title="Hand the class back to its owning teacher">
                  <HandArrowDown size={14} weight="bold" /> Release
                </button>
              ) : (
                <button data-testid={`cover-class-${c.id}`} onClick={() => onCover(c)} disabled={busyId === c.id}
                  className="btn-secondary text-xs flex items-center gap-1 disabled:opacity-50" style={{ padding: "4px 10px" }}
                  title="Temporarily run this class — release it back anytime">
                  <ArrowsLeftRight size={14} weight="bold" /> Cover
                </button>
              )}
              <button data-testid={`claim-class-${c.id}`} onClick={() => onClaim(c, g.teacher.name || g.teacher.email)} disabled={busyId === c.id}
                className="btn-secondary text-xs flex items-center gap-1 disabled:opacity-50"
                style={{ padding: "4px 10px", background: "#FEF3C7", color: "#92400E", borderColor: "#FDE68A" }}
                title="Permanently take this class over — it becomes yours">
                <HandGrabbing size={14} weight="bold" /> Claim
              </button>
            </div>
          </div>
          {openStudents[c.id] && <ClassStudents cls={c} myClasses={myClasses} onChanged={onChanged} />}
        </div>
      ))}
    </div>
  );
}

export default function OtherClassesPanel() {
  const { user } = useAuth();
  const [groups, setGroups] = useState(null);
  const [myClasses, setMyClasses] = useState([]);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setErr("");
    try {
      const [ov, mine] = await Promise.all([api.get("/classes/school-overview"), api.get("/classes")]);
      setGroups(ov.data);
      setMyClasses((mine.data || []).filter((c) => c.teacher_id === user?.id || c.covering_teacher_id === user?.id));
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const run = async (id, fn, okMsg) => {
    setBusyId(id);
    try { await fn(); toast.success(okMsg); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(""); }
  };
  const cover = (c) => run(c.id, () => api.post(`/classes/${c.id}/take-over`), `You're now covering ${c.name}`);
  const release = (c) => run(c.id, () => api.post(`/classes/${c.id}/release`), `${c.name} handed back to its teacher`);
  const claim = (c, teacherName) => {
    if (!window.confirm(`Permanently take over "${c.name}" from ${teacherName}? The class and all its students become yours. ${teacherName} will be notified and can undo this within 24 hours.`)) return;
    run(c.id, () => api.post(`/classes/${c.id}/claim`), `${c.name} is now your class`);
  };
  const coverAll = (g) => {
    const targets = g.classes.filter((c) => !c.covered_by_me);
    if (targets.length === 0) { toast.info("You're already covering all their classes"); return; }
    run(g.teacher.id, () => Promise.all(targets.map((c) => api.post(`/classes/${c.id}/take-over`))),
      `Now covering all ${targets.length} of ${g.teacher.name}'s classes`);
  };
  const claimAll = (g) => {
    if (!window.confirm(`Permanently take over ALL ${g.classes.length} of ${g.teacher.name}'s classes? They become yours. ${g.teacher.name} will be notified and can undo this within 24 hours.`)) return;
    run(g.teacher.id, () => Promise.all(g.classes.map((c) => api.post(`/classes/${c.id}/claim`))),
      `All of ${g.teacher.name}'s classes are now yours`);
  };

  return (
    <div data-testid="other-classes-panel">
      <div className="rounded-2xl border border-[#C4C8CE] bg-white p-4 mb-5">
        <div className="font-bold font-display flex items-center gap-2">
          <UsersThree size={18} weight="duotone" color="#84CC16" /> Other Classes In My School
        </div>
        <p className="text-sm text-[#000000] mt-1">
          <strong>Cover</strong> a colleague's class to run it temporarily (release it back anytime), or <strong>Claim</strong> it to
          take it over permanently. Open <strong>Students</strong> to grab individual students into one of your own classes.
        </p>
      </div>
      {err && <div className="text-[#FF5100] text-sm mb-3" data-testid="other-classes-error">{err}</div>}
      {groups === null && !err && <div className="text-[#000000] text-sm">Loading…</div>}
      {groups && groups.length === 0 && (
        <div className="text-sm text-[#000000] rounded-xl border border-dashed border-[#C4C8CE] p-4">
          No other teachers found at your school yet.
        </div>
      )}
      {(groups || []).map((g) => (
        <TeacherGroup key={g.teacher.id} group={g} myClasses={myClasses} busyId={busyId}
          onCover={cover} onRelease={release} onClaim={claim}
          onCoverAll={coverAll} onClaimAll={claimAll} onChanged={load} />
      ))}
    </div>
  );
}
